import { useEffect, useMemo, useState } from "react"

import { pageSize, SORT_FIELD_BY_KEY } from "../pages/AllPage/constants/allPageData"
import { expandGroupsToCategories } from "../pages/AllPage/constants/categoryGroups"

// Everything the /recover/all list needs to ask the backend for a page: which page and
// ordering are selected, the string key that collapses the whole filter set into one
// dependency, and the effect that refetches when any of it moves. Extracted from the
// page so AllPage reads as layout rather than query plumbing; the behaviour (including
// the deliberately hand-written dependency list) is unchanged.
export const useDisputesQuery = ({ filters, results, pinTick, isShowDemoData, fetchDisputes }) => {
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState("priority")

  const pagedResults = useMemo(() => {
    const rows = Array.isArray(results) ? results : Array.from(results || [])
    // Already-paginated responses (a real backend returns one page) must not be re-sliced.
    if (rows.length <= pageSize) return rows
    const start = (page - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [results, page])

  // Single string key over every filter array so the fetch effect re-runs on any
  // filter change without listing each one.
  const filterKey =
    [
      filters.status,
      filters.submission,
      filters.dispute_type,
      filters.reason_category,
      filters.reason_code,
      filters.flag_reason,
      filters.cycle_stage,
      filters.issuer,
      filters.issuing_bank,
      filters.customer,
      filters.customer_email,
      filters.assignee,
      filters.card_type,
      filters.currency,
      filters.country,
      filters.processor,
      filters.card_last4,
      filters.processor_dispute_id
    ]
      .map((a) => (a || []).join("|"))
      .join("~") +
    `~${filters.date_from || ""}~${filters.date_to || ""}` +
    `~upd:${filters.updated_from || ""}:${filters.updated_to || ""}` +
    `~due:${filters.due_from || ""}:${filters.due_to || ""}` +
    `~txn:${filters.transaction_from || ""}:${filters.transaction_to || ""}` +
    `~amt:${filters.amount?.type || ""}:${filters.amount?.min ?? ""}:${filters.amount?.max ?? ""}` +
    `~enr:${filters.enrichment?.type || ""}:${filters.enrichment?.min ?? ""}:${filters.enrichment?.max ?? ""}`

  // Re-fetch on filter/sort/page change AND when a pin reorders the list (pinTick).
  useEffect(() => {
    fetchDisputes({
      status: filters.status,
      // Submission event type (scheduled / needs_review / sent), see SUBMISSION_DATA.
      submission: filters.submission,
      // Case type (dispute | inquiry). Was missing from BOTH this param map and the
      // filterKey dependency string, so picking a Type changed the chip and nothing else.
      dispute_type: filters.dispute_type,
      reason_category: expandGroupsToCategories(filters.reason_category),
      reason_code: filters.reason_code,
      // Manual-review flag reason -> backend `flag`.
      flag: filters.flag_reason,
      // Network lifecycle stage -> backend `stage`.
      stage: filters.cycle_stage,
      issuer: filters.issuer,
      issuing_bank: filters.issuing_bank,
      customer: filters.customer,
      customer_email: filters.customer_email,
      // Assignee filter -> backend `assigned_to` (team-member ids, or 'unassigned').
      assigned_to: filters.assignee,
      card_type: filters.card_type,
      currency: filters.currency,
      country: filters.country,
      processor: filters.processor,
      card_last4: filters.card_last4,
      processor_dispute_id: filters.processor_dispute_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      updated_from: filters.updated_from,
      updated_to: filters.updated_to,
      due_from: filters.due_from,
      due_to: filters.due_to,
      transaction_from: filters.transaction_from,
      transaction_to: filters.transaction_to,
      // Enrichment range, same param shape as Amount below.
      enrichment_type: filters.enrichment?.type || undefined,
      enrichment_min: filters.enrichment?.min ?? undefined,
      enrichment_max: filters.enrichment?.max ?? undefined,
      amount_type: filters.amount?.type || undefined,
      amount_min: filters.amount?.min ?? undefined,
      amount_max: filters.amount?.max ?? undefined,
      page,
      page_size: pageSize,
      ordering
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterKey, ordering, pinTick, isShowDemoData])

  // antd fires this for both pagination and header-sort changes.
  const handleChangeTable = (pagination, _f, sorter) => {
    if (pagination?.current && pagination.current !== page) setPage(pagination.current)
    let next = "priority"
    if (sorter && sorter.order) {
      // # has no dataIndex, so fall back to its column key mapping.
      const field = SORT_FIELD_BY_KEY[sorter.columnKey] || sorter.field
      if (field) next = sorter.order === "ascend" ? field : `-${field}`
    }
    if (next !== ordering) {
      setOrdering(next)
      setPage(1)
    }
  }

  return { page, setPage, ordering, pagedResults, handleChangeTable }
}

export default useDisputesQuery
